/**
 * NoiseDropdown - Compact noise reduction dropdown next to mic button
 *
 * Displays a small gear icon that opens a dropdown for noise control settings.
 * Positioned inline next to the microphone button (not floating).
 */

import { useState, useEffect, useRef } from 'react';
import { getUserPreferences, updateUserPreferences } from '../services/api';
import type { NoiseReductionType } from '../types/user';

const NOISE_OPTIONS: { label: string; value: NoiseReductionType; description: string }[] = [
  { label: 'None', value: 'none', description: 'No noise filtering' },
  { label: 'Headset', value: 'near_field', description: 'For headphones/earbuds' },
  { label: 'Public', value: 'far_field', description: 'For laptop/room mics' },
];

interface NoiseDropdownProps {
  value: NoiseReductionType;
  onChange: (value: NoiseReductionType) => void;
  disabled?: boolean;
}

export default function NoiseDropdown({ value, onChange, disabled = false }: NoiseDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close when disabled (voice session active)
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  const handleSelect = async (newValue: NoiseReductionType) => {
    if (newValue === value) {
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateUserPreferences(newValue);
      onChange(newValue);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to save noise preference:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const currentLabel = NOISE_OPTIONS.find(o => o.value === value)?.label || 'Public';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute bottom-14 right-0 w-56 bg-gray-800 rounded-lg shadow-xl border border-gray-600 overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-gray-700">
            <span className="text-xs font-medium text-gray-400">Noise Control</span>
          </div>
          <div className="py-1">
            {NOISE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                disabled={isSaving}
                className={`w-full px-3 py-2 flex items-center justify-between hover:bg-gray-700 transition-colors ${
                  isSaving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex flex-col items-start">
                  <span className="text-sm text-white">{option.label}</span>
                  <span className="text-xs text-gray-500">{option.description}</span>
                </div>
                {value === option.value && (
                  <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gear Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
          disabled
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : isOpen
              ? 'bg-gray-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
        }`}
        title={disabled ? 'Settings unavailable while speaking' : `Noise: ${currentLabel}`}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    </div>
  );
}
